import { useRouter } from 'expo-router';
import {
  Platform,
  Pressable,
  type PressableStateCallbackType,
  StyleSheet,
  View,
  type ViewStyle,
} from 'react-native';
import { useTranslation } from '../../contexts/TranslationContext';
import { useResponsive } from '../../hooks/useResponsive';
import { trackEvent } from '../../lib/analytics';
import { useMixpanel } from '../../lib/mixpanel';
import { colors } from '../../theme/colors';
import { spacing } from '../../theme/spacing';
import { tokens } from '../../theme/tokens';
import { LanguageSwitcher } from '../LanguageSwitcher';
import { Button } from '../primitives/Button';
import { Text } from '../primitives/Text';
import { scrollToSection } from './scrollToSection';

const NAV_LINKS = [
  { label: 'Features', target: 'features' },
  { label: 'Workflow', target: 'workflow' },
  { label: 'Pricing', target: 'pricing' },
] as const;

const blur = Platform.select({
  web: { backdropFilter: 'blur(18px)' } as unknown as ViewStyle,
  default: {} as ViewStyle,
});

/**
 * Floating frosted pill nav: wordmark + section links + a persistent login
 * CTA. Absolute overlay above the scroll view (web landing only).
 */
/**
 * Where the signup CTAs lead. Defaults to signup so this stays a pure
 * presentational component: the landing screens own the capacity lookup, and an
 * unanswered gate must never render a waitlist it has not confirmed.
 */
export interface AccessGateCta {
  destination?: '/login' | '/waitlist';
}

export const LandingHeader = ({ destination = '/login' }: AccessGateCta = {}) => {
  const router = useRouter();
  const { t } = useTranslation();
  const mp = useMixpanel();
  const { isMobile } = useResponsive();

  return (
    <View style={styles.header} pointerEvents="box-none">
      <View style={[styles.pill, blur]}>
        <Text variant="button" color={colors.text.primary} style={styles.brand}>
          Flow
          <Text variant="button" color={colors.primary[400]} style={styles.brand}>
            SellAI
          </Text>
        </Text>
        <View style={[styles.nav, isMobile && styles.navMobile]}>
          {/* Section links are in-page anchors; below the mobile breakpoint they
              don't fit alongside the language switcher + login CTA and pushed
              both off the pill, so they collapse out on small screens. */}
          {!isMobile &&
            NAV_LINKS.map((link) => (
              <Pressable
                key={link.target}
                accessibilityRole="link"
                onPress={() => {
                  trackEvent(mp, 'landing.nav_clicked', { target: link.target });
                  scrollToSection(link.target);
                }}
                style={(
                  state: PressableStateCallbackType & { hovered?: boolean; focused?: boolean },
                ) => [(state.hovered || state.focused) && styles.navLinkHover]}
              >
                <Text variant="bodySmall" color={colors.text.secondary}>
                  {link.label}
                </Text>
              </Pressable>
            ))}
          <LanguageSwitcher condensed />
          {/* On phones the wordmark + language switcher already fill the pill; the
              login CTA is dropped here since the hero carries the primary CTA. */}
          {!isMobile && (
            <Button
              testID="landing_header_login_cta"
              variant="primary"
              size="sm"
              pill
              onPress={() => {
                trackEvent(mp, 'landing.login_cta_clicked', {
                  source: 'header',
                  destination,
                });
                router.push(destination);
              }}
            >
              {t('landing.hero.primaryCta')}
            </Button>
          )}
        </View>
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  header: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    zIndex: 1000,
    alignItems: 'center',
    paddingHorizontal: spacing.lg,
    paddingTop: spacing.lg,
  },
  pill: {
    width: '100%',
    maxWidth: 1120,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingVertical: spacing.sm,
    paddingLeft: spacing.lg,
    paddingRight: spacing.sm,
    borderRadius: tokens.radius.full,
    borderWidth: 1,
    borderColor: colors.border.muted,
    backgroundColor: colors.surface.navChrome,
  },
  brand: {
    fontSize: 17,
    fontWeight: '700',
    letterSpacing: -0.3,
  },
  nav: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.xl,
  },
  navMobile: {
    gap: spacing.sm,
  },
  navLinkHover: {
    opacity: 0.65,
  },
});
